/**
 * 主从报表构建器：父数据集按分组列（aggregate:group, expand:down）向下扩展，
 * 每个父行内嵌子数据集表头 + 子数据集数据行（expand:down, 带 conditions 关联父字段）。
 *
 * 布局约定（以 2 列表头为示例，列数随字段数自适应）：
 *  第 1 行：父表表头（部门ID / 部门名称...）
 *  第 2 行：父表数据（group 聚合, down）—— 父格基准行
 *  第 3 行：子表区块标题（跨列合并，如「拥有的员工列表」）
 *  第 4 行：子表表头（员工ID / 员工姓名...）
 *  第 5 行：子表数据（down, 带 conditions 按父字段过滤）
 *  第 6 行：占位空行（保证子表数据扩展后有独立行空间）
 *
 * 关键点：子表数据单元格必须带 conditions，left 为本数据集关联字段，
 * 关联父数据集的父字段（valueType=dataset），否则子表会展示全量数据。
 *
 * 左父格约束（详见 src/references/top-parent-cell.md）：父表数据区向下扩展（expand=down），
 * 其最左列的扩展分组格即「顶层左父格」（自身 left="none"）。若子表单元格与父表数据区未
 * 形成左父格关系，则：
 *  - 父表数据区其余列（col>1）的 left 指向顶层左父格；
 *  - 所有位于顶层左父格左侧列的单元格（父表表头、子表区块标题、子表表头、子表数据、
 *    占位空行等）的 left 全部指向顶层左父格；
 *  - 子表其余列（col>1）保持默认（left="default"）。
 * 本构建器父表数据区从 col=1 开始，故顶层左父格为第 2 行第 1 列（A2）。
 */

import type { ReportIR, CellIR, DatasetIR, DatasourceIR } from '../ir/types.js';
import { coordName } from '../grid/coordinate.js';
import { normalizeTopParentCells } from '../grid/parent.js';

export interface MasterDetailInput {
  name?: string;
  /** 父数据集（如 dept）。 */
  master: {
    datasetName: string;
    /** 父表分组字段（决定按哪个字段去重展开，如 DEPT_ID）。 */
    groupField: { name: string; label?: string; type: DatasetIR['fields'][number]['type'] };
    /** 父表其余展示字段（group 聚合，随父行重复）。 */
    fields: Array<{ name: string; label?: string; type: DatasetIR['fields'][number]['type'] }>;
    sql?: string;
  };
  /** 子数据集（如 employee）。 */
  detail: {
    datasetName: string;
    /** 子表展示字段（select/group 逐行明细）。 */
    fields: Array<{ name: string; label?: string; type: DatasetIR['fields'][number]['type'] }>;
    /** 关联字段：子数据集的字段名（如 DEPT_ID），用于 conditions.left。 */
    joinField: string;
    /** 子表区块标题（缺省「子表列表」）。 */
    title?: string;
    sql?: string;
  };
  datasource: DatasourceIR;
  title?: string;
  paper?: ReportIR['paper'];
}

export function buildMasterDetail(input: MasterDetailInput): ReportIR {
  const { master, detail, datasource } = input;

  // 父表字段 = 分组字段 + 其余字段（按展示顺序，分组列放最前）
  const masterDisplayFields = [
    { name: master.groupField.name, label: master.groupField.label ?? master.groupField.name, type: master.groupField.type },
    ...master.fields.map((f) => ({ name: f.name, label: f.label ?? f.name, type: f.type })),
  ];
  const detailFields = detail.fields.map((f) => ({ name: f.name, label: f.label ?? f.name, type: f.type }));

  // 列数取父、子表字段数的较大值（子表标题跨列合并用）
  const columnCount = Math.max(masterDisplayFields.length, detailFields.length);
  const rowCount = 6; // 1:父头 2:父数据 3:子标题 4:子头 5:子数据 6:占位

  const cells: CellIR[] = [];

  // 顶层左父格：父表数据区最左列的扩展分组格（本布局为第 2 行第 1 列 = A2），自身 left="none"。
  const topLeftCell = coordName(2, 1);

  // 第1行 父表表头（位于顶层左父格左侧列，left 指回顶层左父格，与官方样本 A1.left=B1 同构）
  masterDisplayFields.forEach((f, i) => {
    cells.push({
      row: 1,
      col: i + 1,
      content: { kind: 'text', value: f.label },
      style: { align: 'center', bold: true, bgcolor: '#f2f2f2' },
      left: i === 0 ? topLeftCell : undefined, // 仅最左列需显式指向顶层左父格
    });
  });

  // 第2行 父表数据：分组列 group 聚合 + 其余字段 group 聚合，全部 expand=down
  masterDisplayFields.forEach((f, i) => {
    const col = i + 1;
    cells.push({
      row: 2,
      col,
      content: { kind: 'dataset', dataset: master.datasetName, field: f.name, aggregate: 'group' },
      expand: 'down',
      // 最左列（col=1）即顶层左父格，自身 left="none"；其余父列 left 指向顶层左父格。
      left: col === 1 ? 'none' : topLeftCell,
      style: { align: 'left' },
    });
  });

  // 第3行 子表区块标题（跨列合并，位于顶层左父格左侧列，left 指向顶层左父格）
  cells.push({
    row: 3,
    col: 1,
    content: { kind: 'text', value: detail.title ?? '子表列表' },
    colspan: columnCount,
    left: topLeftCell,
    style: { align: 'center', bgcolor: '#dae5f0' },
  });

  // 第4行 子表表头（与子表相关的表头单元格也要处理：第一列 left 指向顶层左父格）
  detailFields.forEach((f, i) => {
    const col = i + 1;
    cells.push({
      row: 4,
      col,
      content: { kind: 'text', value: f.label },
      style: { align: 'center', bold: true, bgcolor: '#f2f2f2' },
      // 子表表头第一列（col=1）left 指向顶层左父格（官方样本 A4.left=B1），其余列保持 default。
      left: col === 1 ? topLeftCell : undefined,
    });
  });

  // 第5行 子表数据：expand=down + conditions 按父字段过滤
  detailFields.forEach((f, i) => {
    const col = i + 1;
    cells.push({
      row: 5,
      col,
      content: {
        kind: 'dataset',
        dataset: detail.datasetName,
        field: f.name,
        aggregate: 'group',
        conditions: [
          {
            left: detail.joinField,
            op: '==',
            valueType: 'dataset',
            dataset: master.datasetName,
            field: master.groupField.name,
          },
        ],
      },
      expand: 'down',
      // 子表数据第一列（col=1）left 指向顶层左父格（官方样本 A5.left=B1），其余列保持 default。
      left: col === 1 ? topLeftCell : undefined,
    });
  });

  // 第6行 占位空行（保证子表扩展后父表下一行不被覆盖；第一列 left 指回顶层左父格）
  for (let c = 1; c <= columnCount; c++) {
    cells.push({
      row: 6,
      col: c,
      content: { kind: 'text', value: '' },
      left: c === 1 ? topLeftCell : undefined,
    });
  }

  const datasets: DatasetIR[] = [
    {
      name: master.datasetName,
      type: 'sql',
      datasource: datasource.name,
      sql: master.sql ?? `select * from ${master.datasetName}`,
      readFieldRemark: false,
      fields: masterDisplayFields,
    },
    {
      name: detail.datasetName,
      type: 'sql',
      datasource: datasource.name,
      sql: detail.sql ?? `select * from ${detail.datasetName}`,
      readFieldRemark: false,
      fields: [
        // 子表需包含关联字段（供 conditions 过滤）
        { name: detail.joinField, label: detail.joinField, type: 'string' },
        ...detailFields,
      ],
    },
  ];

  return {
    name: input.name ?? '主从报表',
    rowCount,
    columnCount,
    cells: normalizeTopParentCells(cells),
    datasets,
    datasources: [datasource],
    title: input.title ?? 'fileName()',
    paper: input.paper,
  };
}
